/**
 * Serialize / parse filter selections to and from the collection URL query string.
 *
 * Scheme (mirrors Shopify's storefront filter params where possible):
 *   Brand         -> filter.p.vendor=<value>        (repeatable)
 *   Product Type  -> filter.p.product_type=<value>  (repeatable)
 *   Price buckets -> filter.price=<bucket label>     (repeatable; disjoint ranges)
 *   All tag groups-> filter.p.tag=<raw tag>          (repeatable; group resolved by value)
 *
 * Sort is carried separately in `sort_by` (see collection-sort.liquid).
 */

import { FILTER_GROUPS, PRICE_RANGES, getGroup, resolveTag } from './FilterConfig';
import { FilterSelection } from './types';

const PARAM_VENDOR = 'filter.p.vendor';
const PARAM_TYPE = 'filter.p.product_type';
const PARAM_PRICE = 'filter.price';
const PARAM_TAG = 'filter.p.tag';
const PARAM_OPTION_PREFIX = 'filter.v.option.';

function pushValue(selection: FilterSelection, key: string, value: string): void {
  if (!selection[key]) selection[key] = [];
  if (selection[key].indexOf(value) === -1) selection[key].push(value);
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch (e) {
    return s;
  }
}

export function parseQueryString(search: string): FilterSelection {
  const selection: FilterSelection = {};
  const qs = (search || '').replace(/^\?/, '');
  if (!qs) return selection;

  qs.split('&').forEach((pair) => {
    if (!pair) return;
    const idx = pair.indexOf('=');
    if (idx === -1) return;
    const rawKey = safeDecode(pair.slice(0, idx));
    const rawVal = safeDecode(pair.slice(idx + 1).replace(/\+/g, ' '));
    if (!rawVal) return;

    switch (rawKey) {
      case PARAM_VENDOR:
        pushValue(selection, 'brand', rawVal);
        break;
      case PARAM_TYPE:
        pushValue(selection, 'product_type', rawVal);
        break;
      case PARAM_PRICE: {
        // Strip legacy money-formatting markup with plain splits, NOT a regex — an
        // HTML-stripping regex is an incomplete sanitizer (CodeQL
        // js/incomplete-multi-character-sanitization). Only a known PRICE_RANGES key is kept.
        const candidate = rawVal
          .split("<span class='money'>")
          .join('')
          .split('</span>')
          .join('')
          .trim();
        const priceKey = Object.keys(PRICE_RANGES).filter((k) => k === candidate)[0];
        if (priceKey) pushValue(selection, 'price', priceKey);
        break;
      }
      case PARAM_TAG: {
        const resolved = resolveTag(rawVal);
        if (resolved) pushValue(selection, resolved.group.key, resolved.value);
        break;
      }
      default: {
        if (rawKey.indexOf(PARAM_OPTION_PREFIX) === 0) {
          const optionName = rawKey.slice(PARAM_OPTION_PREFIX.length);
          const group = FILTER_GROUPS.filter(
            (g) => g.type === 'variantOption' && g.optionName === optionName
          )[0];
          if (group) pushValue(selection, group.key, rawVal);
        }
        break;
      }
    }
  });

  return selection;
}

export function toQueryString(
  selection: FilterSelection,
  extraParams?: Record<string, string>
): string {
  const parts: string[] = [];
  const enc = (k: string, v: string) => encodeURIComponent(k) + '=' + encodeURIComponent(v);

  Object.keys(selection || {}).forEach((key) => {
    const group = getGroup(key);
    if (!group) return;
    (selection[key] || []).forEach((value) => {
      if (!value) return;
      if (group.type === 'vendor') parts.push(enc(PARAM_VENDOR, value));
      else if (group.type === 'productType') parts.push(enc(PARAM_TYPE, value));
      else if (group.type === 'price') parts.push(enc(PARAM_PRICE, value));
      else if (group.type === 'variantOption')
        parts.push(enc(PARAM_OPTION_PREFIX + (group.optionName || ''), value));
      else {
        const tag =
          group.tagPrefix === 'FILTER'
            ? 'Filter' + value.replace(/\s+/g, '')
            : (group.tagPrefix || '') + value;
        parts.push(enc(PARAM_TAG, tag));
      }
    });
  });

  if (extraParams) {
    Object.keys(extraParams).forEach((k) => {
      if (extraParams[k]) parts.push(enc(k, extraParams[k]));
    });
  }

  return parts.join('&');
}
